/**
 * Realtime hub contracts (02-architecture.md "Realtime hub", 05-api.md "Realtime"):
 * rooms, the socket message, the commit-ordered cursor and the catch-up endpoint
 * `GET /v1/outlets/{o}/changes`. Shared by the server and the apps, so room names
 * and cursors are built and parsed the same way everywhere.
 *
 * Socket.io namespace `/ws` (engine path `/socket.io/`). Connect with
 * `auth: { token, terminalId? }` (staff access token; `terminalId` like the
 * X-Terminal-Id header) or `auth: { tableToken }` (dine-in QR, storefront M3: refused
 * for now with `TOKEN_KIND_NOT_SUPPORTED`).
 *
 * Client protocol (no gaps; see docs/realtime.md):
 *  1. Connect and `join` the rooms (ack lists joined and denied rooms).
 *  2. Then `GET {o}/changes?since=<cursor>` (repeat while `hasMore`); apply the
 *     messages, de-duplicating by (eventId, room). Keep the final page's `nextCursor`
 *     (`laterCursor`): when nothing happened it is the same position re-stamped now,
 *     which keeps a quiet outlet's cursor inside the 24 h window.
 *  3. Apply every socket `message` the same way. Messages arrive in cursor order.
 *     Persist the later of the cursor you hold and each message's cursor
 *     (`laterCursor`), but only once step 2 has finished for this connection (a
 *     socket message can overtake the catch-up).
 *  4. On reconnect, repeat from 1 with the persisted cursor. `410 CURSOR_EXPIRED`
 *     (cursor older than 24 h, unknown, or past retention): refetch the full state, after first
 *     taking a fresh cursor (`GET {o}/changes` without `since`).
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { isUuid, uuidSchema, type Uuid } from './ids.js';

/** Socket.io namespace of the realtime hub. */
export const REALTIME_NAMESPACE = '/ws';

/** Catch-up serves events published within this window. */
export const CATCH_UP_WINDOW_HOURS = 24;

/** Socket event names. */
export const SOCKET_EVENTS = {
  /** server -> client: a RealtimeMessage. */
  message: 'message',
  /** client -> server: `{ rooms }`, ack JoinResult. */
  join: 'join',
  /** client -> server: `{ rooms }`, ack `{ left }`. */
  leave: 'leave',
  /** server -> client: `{ rooms }` the client lost access to (role change) and was removed from. */
  roomsRevoked: 'rooms_revoked',
  /** server -> client, right before the server disconnects it: `{ code }`. */
  signedOut: 'signed_out',
} as const;

/**
 * Error codes in a refused handshake (`connect_error`, `err.data.code`) and in
 * `signed_out`: `UNAUTHENTICATED` (missing, invalid or expired token, session ended,
 * terminal blocked: refresh the token, else sign in again), `TOKEN_EXPIRED` (the
 * access token ran out while connected: refresh and reconnect),
 * `TOKEN_KIND_NOT_SUPPORTED` (customer or table token; storefront M3),
 * `SERVICE_UNAVAILABLE` (server shutting down, or it lost its Redis subscription and
 * the socket may have missed messages: reconnect and catch up).
 */
export const REALTIME_ERROR_CODES = [
  'UNAUTHENTICATED',
  'TOKEN_EXPIRED',
  'TOKEN_KIND_NOT_SUPPORTED',
  'SERVICE_UNAVAILABLE',
] as const;
export type RealtimeErrorCode = (typeof REALTIME_ERROR_CODES)[number];

/**
 * JWT audience of customer tokens (storefront: a customer signed in with a WhatsApp code, M5
 * task 6). A socket `{ token }` with this audience is a customer-app socket: the menu rooms of
 * its company's outlets and the `order:{id}` rooms of orders placed under its account.
 */
export const CUSTOMER_TOKEN_AUDIENCE = 'qrkey-store';

// --- rooms ------------------------------------------------------------------

/**
 * - `pos`: tills and captains of an outlet (order changes, table status, sold-out, …)
 * - `kds`: one KDS station (tickets, bumps, recalls)
 * - `menu`: open tills, QR menus (availability, menu version)
 * - `order`: the diner of one order (storefront, M3)
 * - `terminal`: one device (force logout, block, min-version, print jobs)
 * - `session`: INTERNAL, one sign-in session (force logout); joined by the server
 *   at connect, never joinable and never served by catch-up
 */
export type RealtimeRoom =
  | { readonly kind: 'pos'; readonly outletId: Uuid }
  | { readonly kind: 'kds'; readonly outletId: Uuid; readonly stationId: Uuid }
  | { readonly kind: 'menu'; readonly outletId: Uuid }
  | { readonly kind: 'order'; readonly orderId: Uuid }
  | { readonly kind: 'terminal'; readonly terminalId: Uuid }
  | { readonly kind: 'session'; readonly sessionId: Uuid };

export type RealtimeRoomKind = RealtimeRoom['kind'];

/** Rooms whose name starts with `outlet:{id}:`. */
export const OUTLET_ROOM_KINDS = ['pos', 'kds', 'menu'] as const;

export const realtimeRoom = {
  pos: (outletId: Uuid): RealtimeRoom => ({ kind: 'pos', outletId }),
  kds: (outletId: Uuid, stationId: Uuid): RealtimeRoom => ({ kind: 'kds', outletId, stationId }),
  menu: (outletId: Uuid): RealtimeRoom => ({ kind: 'menu', outletId }),
  order: (orderId: Uuid): RealtimeRoom => ({ kind: 'order', orderId }),
  terminal: (terminalId: Uuid): RealtimeRoom => ({ kind: 'terminal', terminalId }),
  session: (sessionId: Uuid): RealtimeRoom => ({ kind: 'session', sessionId }),
};

/** The room's wire name, e.g. `outlet:{id}:pos`. */
export function roomName(room: RealtimeRoom): string {
  switch (room.kind) {
    case 'pos':
    case 'menu':
      return `outlet:${room.outletId}:${room.kind}`;
    case 'kds':
      return `outlet:${room.outletId}:kds:${room.stationId}`;
    case 'order':
      return `order:${room.orderId}`;
    case 'terminal':
      return `terminal:${room.terminalId}`;
    case 'session':
      return `session:${room.sessionId}`;
  }
}

/** The room a wire name denotes, or null when it is not a room name. Ids must be lowercase. */
export function parseRoom(name: unknown): RealtimeRoom | null {
  if (typeof name !== 'string' || name.length > 120) return null;
  const parts = name.split(':');
  const lower = (id: string | undefined): id is Uuid =>
    id !== undefined && isUuid(id) && id === id.toLowerCase();
  const [head, a, b, c, ...rest] = parts;
  if (rest.length > 0) return null;
  if (head === 'outlet' && lower(a)) {
    if ((b === 'pos' || b === 'menu') && c === undefined) return { kind: b, outletId: a };
    if (b === 'kds' && lower(c)) return { kind: 'kds', outletId: a, stationId: c };
    return null;
  }
  if (b !== undefined || c !== undefined) return null;
  if (head === 'order' && lower(a)) return { kind: 'order', orderId: a };
  if (head === 'terminal' && lower(a)) return { kind: 'terminal', terminalId: a };
  if (head === 'session' && lower(a)) return { kind: 'session', sessionId: a };
  return null;
}

/** The outlet a room belongs to (outlet rooms only). */
export function roomOutlet(room: RealtimeRoom): Uuid | null {
  return room.kind === 'pos' || room.kind === 'kds' || room.kind === 'menu' ? room.outletId : null;
}

export const roomNameSchema = z
  .string()
  .max(120)
  .refine((v) => parseRoom(v) !== null, { message: 'Not a realtime room name' });

// --- cursor -----------------------------------------------------------------

/**
 * `<position>-<publishedAtMs>`: the event's commit-ordered position
 * (platform.outbox_event.position) and when it was published (or, for a catch-up
 * `nextCursor` with nothing new, when it was issued). Clients store it as a string
 * and compare two cursors only with `compareCursors` / `laterCursor` (by position,
 * then time; the time only lets the server answer 410 for a cursor older than the
 * catch-up window).
 */
export const REALTIME_CURSOR_PATTERN = /^(0|[1-9]\d{0,18})-(\d{1,15})$/;

export const realtimeCursorSchema = z
  .string()
  .max(40)
  .regex(REALTIME_CURSOR_PATTERN, { message: 'Not a realtime cursor' });

export interface RealtimeCursor {
  readonly position: bigint;
  /** Epoch milliseconds the position was published (or, for a resume cursor, issued). */
  readonly atMs: number;
}

const MAX_POSITION = 2n ** 63n - 1n;

export function encodeCursor(cursor: RealtimeCursor): string {
  if (cursor.position < 0n || cursor.position > MAX_POSITION) {
    throw new RangeError('cursor position out of range');
  }
  if (!Number.isSafeInteger(cursor.atMs) || cursor.atMs < 0) {
    throw new RangeError('cursor time out of range');
  }
  return `${cursor.position}-${cursor.atMs}`;
}

/** The cursor, or null when `value` is not one. */
export function decodeCursor(value: unknown): RealtimeCursor | null {
  if (typeof value !== 'string') return null;
  const m = REALTIME_CURSOR_PATTERN.exec(value);
  if (!m) return null;
  const position = BigInt(m[1] ?? '');
  const atMs = Number(m[2]);
  if (position > MAX_POSITION || !Number.isSafeInteger(atMs)) return null;
  return { position, atMs };
}

/**
 * Order of two cursors (-1, 0, 1): by position (which one resumes later), then by
 * time, so a re-stamped cursor (same position, issued later) orders after the one it
 * renews.
 */
export function compareCursors(a: string, b: string): -1 | 0 | 1 {
  const x = decodeCursor(a);
  const y = decodeCursor(b);
  if (!x || !y) throw new TypeError('Not a realtime cursor');
  if (x.position !== y.position) return x.position < y.position ? -1 : 1;
  return x.atMs < y.atMs ? -1 : x.atMs > y.atMs ? 1 : 0;
}

/**
 * The cursor to persist: the later of the one held and a new one (a socket message's
 * `cursor` or a catch-up `nextCursor`). Never moves back in position; at the same
 * position it takes the fresher time, so an idle client's cursor stays in the window.
 */
export function laterCursor(held: string, next: string): string {
  return compareCursors(next, held) > 0 ? next : held;
}

// --- messages ---------------------------------------------------------------

/**
 * What a client receives on a socket (`message`) and from catch-up: one projection
 * of one outbox event for one room. `type` is a PROJECTION type (`terminal.blocked`,
 * later `order.updated`, `table.status_changed`, …), not necessarily the event type;
 * docs/realtime.md maps events to projections. `data` is shaped per room and never
 * carries personal data to outlet-wide rooms.
 */
export const realtimeMessageSchema = z.object({
  type: z.string().min(1).max(100),
  room: z.string().max(120),
  outletId: uuidSchema.nullable(),
  data: z.record(z.string(), z.unknown()),
  /** The outbox event this projects; de-duplicate by (eventId, room). */
  eventId: uuidSchema,
  /** When the event happened (its occurredAt). */
  at: isoTimestampSchema,
  /** Resume point: catch-up `since=` this returns only later messages. */
  cursor: realtimeCursorSchema,
});
export type RealtimeMessage = z.output<typeof realtimeMessageSchema>;

/** Most rooms one join/leave call (and one socket) may hold, beyond the server-joined ones. */
export const MAX_ROOMS_PER_SOCKET = 50;

export const roomsRequestSchema = z.object({
  rooms: z.array(z.string().max(120)).min(1).max(MAX_ROOMS_PER_SOCKET),
});

export const joinResultSchema = z.object({
  joined: z.array(z.string()),
  denied: z.array(
    z.object({
      room: z.string(),
      /** `INVALID_ROOM`, `FORBIDDEN` (outlet or permission), `NOT_SUPPORTED` (customer rooms), `TOO_MANY_ROOMS`. */
      code: z.enum(['INVALID_ROOM', 'FORBIDDEN', 'NOT_SUPPORTED', 'TOO_MANY_ROOMS']),
      requiredPermission: z.string().optional(),
    }),
  ),
});
export type JoinResult = z.output<typeof joinResultSchema>;

// --- catch-up ---------------------------------------------------------------

const DIGITS = /^\d+$/;

export const CHANGES_DEFAULT_LIMIT = 200;
export const CHANGES_MAX_LIMIT = 500;

/** `GET {o}/changes?since=&room=&limit=` */
export const changesQuerySchema = z.object({
  /** A cursor from a socket message or a previous catch-up. Absent: just return the current cursor. */
  since: realtimeCursorSchema.optional(),
  /**
   * Comma-separated room names of this outlet (`outlet:{o}:pos,outlet:{o}:menu`) or
   * your terminal (`terminal:{id}`). Absent: every room of the outlet you may join
   * (every KDS station when you hold kitchen.kds).
   */
  room: z
    .string()
    .max(2_000)
    .transform((v) => [
      ...new Set(
        v
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean),
      ),
    ])
    .pipe(z.array(roomNameSchema).min(1).max(MAX_ROOMS_PER_SOCKET))
    .optional(),
  /** Events scanned per call (messages may be fewer). */
  limit: z.preprocess(
    (v) => (typeof v === 'string' && DIGITS.test(v.trim()) ? Number(v.trim()) : v),
    z.number().int().min(1).max(CHANGES_MAX_LIMIT).default(CHANGES_DEFAULT_LIMIT),
  ),
});
export type ChangesQuery = z.output<typeof changesQuerySchema>;

export const changesResponseSchema = z.object({
  /** Messages after `since`, in cursor order. */
  data: z.array(realtimeMessageSchema),
  /** Resume from here (always present; equals `since` when nothing happened). */
  nextCursor: realtimeCursorSchema,
  /** More events are waiting: call again with `nextCursor` at once. */
  hasMore: z.boolean(),
});
export type ChangesResponse = z.output<typeof changesResponseSchema>;
